/**
 * ListTableRow - One list entry as a CSS-grid row
 *
 * Uses `display: contents` so every cell is a direct child of the table grid and columns
 * stay aligned across groups.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { FaTimes } from 'react-icons/fa';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import InlineEditText from '@/components/InlineEditText/InlineEditText';
import QuantityStepper from '@/components/QuantityStepper/QuantityStepper';
import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';
import { fullCardCacheAtom } from '@/state/cardCache';
import { cardClassificationByOracleIdAtom } from '@/state/cardClassification';
import { PRIORITY_LABELS, type EnrichedListCard, type ListPriority } from '@/types/lists';

import { LIST_PRIORITY_BADGE, LIST_PRIORITY_ORDER } from '../lib/listPriority';
import type { ListColumnVisibility } from '../state/listViewSettings';

export interface ListTableRowProps {
  card: EnrichedListCard;
  columnVisibility: ListColumnVisibility;
  isPending: boolean;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove: () => void;
  onPriorityChange: (priority: ListPriority) => void;
  onNotesChange: (notes: string | null) => void;
  onOpenDetails: () => void;
}

const CELL_BASE = 'flex min-w-0 items-center px-2 py-1.5 transition-colors group-hover:bg-zinc-800/50';

export default function ListTableRow({
  card,
  columnVisibility,
  isPending,
  onIncrease,
  onDecrease,
  onRemove,
  onPriorityChange,
  onNotesChange,
  onOpenDetails,
}: ListTableRowProps): ReactElement {
  const { settings } = useUserSettings();
  const fullCard = useAtomValue(fullCardCacheAtom).get(card.scryfall_id);
  const classification = useAtomValue(cardClassificationByOracleIdAtom).get(card.oracle_id);

  // The row itself is `display: contents`, so it generates no box to dim — the cells carry it.
  const cellBase = clsx(CELL_BASE, isPending && 'opacity-50');

  return (
    <div className="group contents">
      {/* Quantity */}
      <div className={clsx(cellBase, 'justify-center rounded-l-lg')}>
        <QuantityStepper
          orientation="vertical"
          value={card.quantity}
          onIncrease={onIncrease}
          onDecrease={onDecrease}
          disabled={isPending}
          increaseLabel={`Increase quantity of ${card.name}`}
          decreaseLabel={card.quantity <= 1 ? `Remove ${card.name} from list` : `Decrease quantity of ${card.name}`}
        />
      </div>

      {/* Thumbnail */}
      <div className={clsx(cellBase, 'justify-center')}>
        <CardHoverPreview imageUris={card.sample_image_uris} name={card.name} layout={fullCard?.layout} cardFaces={fullCard?.card_faces}>
          <button
            type="button"
            onClick={onOpenDetails}
            className="focus-visible:ring-aether shrink-0 rounded focus:outline-none focus-visible:ring-2"
            aria-label={`View details for ${card.name}`}
          >
            <CardImage imageUris={card.sample_image_uris} name={card.name} size="small" className="h-14! w-10!" />
          </button>
        </CardHoverPreview>
      </div>

      {/* Name */}
      <div className={clsx(cellBase, 'min-w-0')}>
        <button
          type="button"
          onClick={onOpenDetails}
          className="hover:text-aether focus-visible:ring-aether truncate rounded text-left text-sm font-medium text-zinc-200 transition-colors focus:outline-none focus-visible:ring-2"
        >
          {card.name}
        </button>
      </div>

      {/* Set */}
      {columnVisibility.set && (
        <div className={clsx(cellBase, 'justify-end text-xs text-zinc-400 tabular-nums')}>
          {fullCard ? (
            <span className="truncate">
              {fullCard.set.toUpperCase()} · {fullCard.collector_number}
            </span>
          ) : (
            <span className="text-zinc-600">—</span>
          )}
        </div>
      )}

      {/* Type */}
      {columnVisibility.type && (
        <div className={clsx(cellBase, 'justify-end text-xs text-zinc-400')}>
          <span className="truncate">{classification?.main_type ?? <span className="text-zinc-600">—</span>}</span>
        </div>
      )}

      {/* Priority */}
      {columnVisibility.priority && (
        <div className={clsx(cellBase)}>
          <select
            value={card.priority}
            onChange={(e) => onPriorityChange(e.target.value as ListPriority)}
            disabled={isPending}
            aria-label={`Priority for ${card.name}`}
            className={clsx(
              'focus-visible:ring-aether w-full cursor-pointer rounded-full bg-transparent px-2 py-0.5 text-xs font-medium ring-1 focus:outline-none focus-visible:ring-2',
              LIST_PRIORITY_BADGE[card.priority],
            )}
          >
            {LIST_PRIORITY_ORDER.map((priority) => (
              <option key={priority} value={priority} className="bg-obsidian text-pearl">
                {PRIORITY_LABELS[priority]}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Notes */}
      {columnVisibility.notes && (
        <div className={clsx(cellBase, 'min-w-0')}>
          <InlineEditText
            value={card.notes}
            onCommit={onNotesChange}
            placeholder="Add note…"
            ariaLabel={`Notes for ${card.name}`}
            disabled={isPending}
          />
        </div>
      )}

      {/* Ownership */}
      {columnVisibility.ownership && (
        <div className={clsx(cellBase, 'flex-col items-end justify-center gap-0.5 text-xs')}>
          <span className={card.owned_quantity > 0 ? 'text-emerald-400' : 'text-zinc-500'}>Owned {card.owned_quantity}</span>
          <span className="text-zinc-500">
            {card.owned_other_editions > 0 && <span className="mr-2">+{card.owned_other_editions} other</span>}
            Available {card.available_quantity}
          </span>
        </div>
      )}

      {/* Price */}
      {columnVisibility.price && (
        <div className={clsx(cellBase, 'justify-end font-mono text-xs text-zinc-300 tabular-nums')}>
          {card.total_price != null ? formatPrice(card.total_price, settings.currency) : <span className="text-zinc-600">—</span>}
        </div>
      )}

      {/* Remove */}
      <div className={clsx(cellBase, 'justify-center rounded-r-lg')}>
        <button
          type="button"
          onClick={onRemove}
          disabled={isPending}
          className="focus-visible:ring-aether rounded p-1.5 text-zinc-500 transition-colors hover:text-red-400 focus:outline-none focus-visible:ring-2 disabled:opacity-40"
          aria-label={`Remove ${card.name} from list`}
        >
          <FaTimes className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
